import { Button } from '@/components/ui/Button';
import {
  BatchEditionResult,
  EditionVisualizeBatchRequest,
} from '@/services/editions.service';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  FolderOpen,
  X,
} from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  construireClasseurXlsx,
  FeuilleXlsx,
  telechargerClasseurXlsx,
} from '../utils/export-xlsx';

interface EditionVisualizationProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuid: string;
  batchResults: BatchEditionResult[];
  request: EditionVisualizeBatchRequest | null;
  isLoading: boolean;
  error: Error | null;
}

const MONTHS = [
  'Janvier',
  'Fevrier',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Aout',
  'Septembre',
  'Octobre',
  'Novembre',
  'Decembre',
];

export function EditionVisualization({
  isOpen,
  onClose,
  batchResults,
  request,
  isLoading,
  error,
}: EditionVisualizationProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'summary' | 'monthly'>('summary');
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);
  const [selectedEditionIndex, setSelectedEditionIndex] = useState(0);

  const successfulResults = batchResults.filter(r => r.success && r.data);
  const currentResult = successfulResults[selectedEditionIndex];
  const currentData = currentResult?.data;

  const handleClose = () => {
    setActiveTab('summary');
    setSelectedMonthIndex(0);
    setSelectedEditionIndex(0);
    onClose();
  };

  const formatValue = (value: string | number): string => {
    if (!value || value === '') return '-';
    if (typeof value === 'number') {
      return value.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return value;
  };

  const getMonthLabel = useCallback((month: number, year: number): string => {
    return `${MONTHS[month - 1]} ${year}`;
  }, []);

  // Detecter dynamiquement les cles "code" et "label" a partir des donnees
  // L'API REST retourne "Compte"/"Libelle" mais Puppeteer retournait "Code"/"Libelle"
  const detectCodeLabelKeys = (
    data: { [key: string]: string | number }[]
  ): { codeKey: string; labelKey: string } => {
    if (data.length === 0) return { codeKey: 'Code', labelKey: 'Libelle' };
    const keys = Object.keys(data[0]);
    // La premiere colonne est le code, la deuxieme le libelle
    return { codeKey: keys[0] || 'Code', labelKey: keys[1] || 'Libelle' };
  };

  const getMonthlyDataForRow = useCallback(
    (
      rowCode: string,
      rowLabel: string,
      codeKey: string,
      labelKey: string
    ): { [key: string]: string }[] => {
      if (!currentData?.monthlyData) return [];

      const monthlyDetails: { [key: string]: string }[] = [];

      currentData.monthlyData.forEach(monthData => {
        const matchingRow = monthData.data.find(
          (row: { [key: string]: string }) => {
            const code = row[codeKey] || '';
            const label = row[labelKey] || '';
            if (rowCode) {
              return String(code) === String(rowCode);
            }
            if (!rowCode && rowLabel) {
              return (
                String(label) === String(rowLabel) &&
                (code === '' || code === undefined)
              );
            }
            return false;
          }
        );

        if (matchingRow) {
          monthlyDetails.push({
            ...matchingRow,
            Mois: getMonthLabel(monthData.month, monthData.year),
          });
        }
      });

      return monthlyDetails;
    },
    [currentData?.monthlyData, getMonthLabel]
  );

  const getSynthesis = (): { [key: string]: string }[] => {
    if (!currentData?.synthesis?.data) return [];
    return currentData.synthesis.data as { [key: string]: string }[];
  };

  const exportCurrentEditionToExcel = useCallback(async () => {
    if (!currentData) return;

    const editionName = currentData.editionName || 'Edition';
    const feuilles: FeuilleXlsx[] = [];

    // Synthesis sheet
    if (currentData.synthesis?.data) {
      const synthesisData = currentData.synthesis.data as {
        [key: string]: string;
      }[];
      if (synthesisData.length > 0) {
        const { codeKey, labelKey } = detectCodeLabelKeys(synthesisData);
        const hasMultiMonth =
          currentData.monthlyData && currentData.monthlyData.length > 1;
        const synthesisSheetData: Record<string, unknown>[] = [];

        synthesisData.forEach((row, rowIndex) => {
          const rowCode = String(row[codeKey] || '');
          const rowLabel = String(row[labelKey] || '');

          if (hasMultiMonth) {
            const monthlyData = getMonthlyDataForRow(
              rowCode,
              rowLabel,
              codeKey,
              labelKey
            );
            monthlyData.forEach(monthRow => {
              synthesisSheetData.push(monthRow);
            });
          }

          const synthesisRow: { [key: string]: unknown } = {};
          Object.keys(row).forEach(key => {
            synthesisRow[key] = row[key];
          });
          if (hasMultiMonth) {
            synthesisRow['Mois'] = t(
              'editions.visualization.average',
              'Moyenne'
            );
          }
          synthesisSheetData.push(synthesisRow);
          if (rowIndex < synthesisData.length - 1) {
            synthesisSheetData.push({});
          }
        });

        feuilles.push({ nom: 'Synthese', lignes: synthesisSheetData });
      }
    }

    // Monthly sheets
    if (currentData.monthlyData) {
      currentData.monthlyData.forEach(monthData => {
        const monthLabel = getMonthLabel(monthData.month, monthData.year);
        feuilles.push({ nom: monthLabel, lignes: monthData.data });
      });
    }

    const fileName = `${editionName}_${request?.startMonth}-${request?.startYear}_${request?.endMonth}-${request?.endYear}.xlsx`;
    try {
      const blob = await construireClasseurXlsx(feuilles);
      telechargerClasseurXlsx(blob, fileName);
    } catch {
      toast.error(
        t(
          'editions.visualization.exportError',
          "L'export Excel n'a pas pu être généré. Réessayez."
        )
      );
    }
  }, [currentData, request, getMonthLabel, getMonthlyDataForRow, t]);

  // exportAllEditionsToExcel - temporarily disabled (see JSX below)
  // const exportAllEditionsToExcel = useCallback(() => { ... }, [successfulResults, request]);

  const renderSummaryTable = (data: { [key: string]: string }[]) => {
    if (data.length === 0) return null;

    const { codeKey, labelKey } = detectCodeLabelKeys(data);
    const rawKeys = Object.keys(data[0]);
    const hasMultipleMonths =
      currentData?.monthlyData && currentData.monthlyData.length > 1;
    const displayKeys = hasMultipleMonths
      ? ['Mois', ...rawKeys.filter(key => key !== 'Mois')]
      : rawKeys;
    const columns = displayKeys.map(key => ({
      key,
      label: key,
      width: key === 'Mois' ? 'w-28' : 'w-20',
    }));

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-primary-600 text-white">
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`${column.width} border-primary-500 border px-3 py-2 text-left text-sm font-medium`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => {
              const rowCode = String(row[codeKey] || '');
              const rowLabel = String(row[labelKey] || '');
              const monthlyData = hasMultipleMonths
                ? getMonthlyDataForRow(rowCode, rowLabel, codeKey, labelKey)
                : [];

              if (hasMultipleMonths && monthlyData.length > 0) {
                return (
                  <React.Fragment key={rowIndex}>
                    {/* Monthly data rows for this block */}
                    {monthlyData.map((monthRow, monthIndex) => (
                      <tr
                        key={`${rowCode || rowLabel}-${monthIndex}`}
                        className={`${
                          monthIndex % 2 === 0 ? 'bg-neutral-50' : 'bg-white'
                        } border-b border-neutral-200`}
                      >
                        {columns.map(column => (
                          <td
                            key={column.key}
                            className={`${column.width} border-r border-neutral-200 px-3 py-2 text-sm last:border-r-0`}
                          >
                            <span
                              className={
                                !monthRow[column.key] ? 'text-neutral-400' : ''
                              }
                            >
                              {formatValue(monthRow[column.key])}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}

                    {/* Synthesis row for this block */}
                    <tr className="border-b border-neutral-300 bg-neutral-100">
                      {columns.map(column => (
                        <td
                          key={column.key}
                          className={`${column.width} border-r border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 last:border-r-0`}
                        >
                          {column.key === 'Mois' ? (
                            <span className="py-2 text-sm font-semibold text-neutral-700">
                              {t('editions.visualization.average', 'Moyenne')}
                            </span>
                          ) : (
                            formatValue(row[column.key])
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* Separator between blocks */}
                    {rowIndex < data.length - 1 && (
                      <tr className="bg-neutral-200">
                        <td colSpan={columns.length} className="h-2"></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              }

              // Single month or no monthly breakdown: render synthesis row directly
              return (
                <tr
                  key={rowIndex}
                  className={`${
                    rowIndex % 2 === 0 ? 'bg-neutral-50' : 'bg-white'
                  } border-b border-neutral-200`}
                >
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className={`${column.width} border-r border-neutral-200 px-3 py-2 text-sm last:border-r-0`}
                    >
                      <span
                        className={!row[column.key] ? 'text-neutral-400' : ''}
                      >
                        {formatValue(row[column.key])}
                      </span>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTable = (data: { [key: string]: string }[]) => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0]);
    const columns = keys.map(key => ({
      key,
      label: key,
      width: 'w-20',
    }));

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-primary-600 text-white">
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`${column.width} border-primary-500 border px-3 py-2 text-left text-sm font-medium`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                className={`${
                  index % 2 === 0 ? 'bg-neutral-50' : 'bg-white'
                } border-b border-neutral-200`}
              >
                {columns.map(column => (
                  <td
                    key={column.key}
                    className={`${column.width} border-r border-neutral-200 px-3 py-2 text-sm last:border-r-0`}
                  >
                    <span
                      className={!row[column.key] ? 'text-neutral-400' : ''}
                    >
                      {formatValue(row[column.key])}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isOpen) return null;

  const failedResults = batchResults.filter(r => !r.success);
  const gedResults = batchResults.filter(r => r.savedToGed);

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 flex flex-col rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-200 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">
                {successfulResults.length > 1
                  ? t(
                      'editions.visualization.titleMulti',
                      '{{count}} editions',
                      { count: successfulResults.length }
                    )
                  : currentData?.editionName || ''}
                {request &&
                  `: ${request.startMonth}/${request.startYear} - ${request.endMonth}/${request.endYear}`}
              </h2>
              <div className="flex items-center gap-3 text-sm text-neutral-500">
                <span>
                  {t(
                    'editions.visualization.description',
                    "Apercu des donnees d'edition"
                  )}
                </span>
                {gedResults.length > 0 && (
                  <span className="text-success-600 flex items-center gap-1">
                    <FolderOpen className="h-3.5 w-3.5" />
                    {t(
                      'editions.visualization.savedToGed',
                      '{{count}} PDF(s) dans la GED',
                      { count: gedResults.length }
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* {successfulResults.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={exportAllEditionsToExcel}
                disabled={!currentData || isLoading}
              >
                <Download className="mr-2 h-4 w-4" />
                {t('editions.downloadAll', 'Tout exporter')}
              </Button>
            )} */}
            <Button
              variant="outline"
              size="sm"
              onClick={exportCurrentEditionToExcel}
              disabled={!currentData || isLoading}
            >
              <Download className="mr-2 h-4 w-4" />
              {t('editions.download', 'Telecharger Excel')}
            </Button>
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="border-primary-600 mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-t-transparent"></div>
                <p className="text-sm text-neutral-600">
                  {t(
                    'editions.visualization.loading',
                    'Chargement des donnees...'
                  )}
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="bg-error-100 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                  <X className="text-error-600 h-6 w-6" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                  {t(
                    'editions.visualization.error.title',
                    'Erreur de chargement'
                  )}
                </h3>
                <p className="text-sm text-neutral-600">
                  {t(
                    'editions.visualization.error.description',
                    'Impossible de charger les donnees de visualisation'
                  )}
                </p>
              </div>
            </div>
          ) : successfulResults.length > 0 ? (
            <div className="flex h-full flex-col">
              {/* Edition selector (multi-edition) */}
              {successfulResults.length > 1 && (
                <div className="flex items-center gap-2 border-b border-neutral-200 px-6 py-3">
                  {successfulResults.map((result, index) => (
                    <button
                      key={result.editionValue}
                      onClick={() => {
                        setSelectedEditionIndex(index);
                        setSelectedMonthIndex(0);
                        setActiveTab('summary');
                      }}
                      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        selectedEditionIndex === index
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                      }`}
                    >
                      {result.data?.editionName || result.editionValue}
                      {result.savedToGed && (
                        <FolderOpen className="ml-1.5 inline h-3.5 w-3.5" />
                      )}
                    </button>
                  ))}

                  {failedResults.length > 0 && (
                    <span className="text-error-600 ml-auto text-xs">
                      {t(
                        'editions.visualization.failedCount',
                        '{{count}} en erreur',
                        { count: failedResults.length }
                      )}
                    </span>
                  )}
                </div>
              )}

              {/* Tabs */}
              <div className="flex border-b border-neutral-200">
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`border-b-2 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'summary'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  {t('editions.visualization.summary', 'Synthese')}
                </button>
                {currentData &&
                  currentData.monthlyData &&
                  currentData.monthlyData.length > 1 && (
                    <button
                      onClick={() => setActiveTab('monthly')}
                      className={`border-b-2 px-6 py-3 text-sm font-medium transition-colors ${
                        activeTab === 'monthly'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-neutral-500 hover:text-neutral-700'
                      }`}
                    >
                      {t('editions.visualization.monthly', 'Par mois')}
                    </button>
                  )}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-hidden">
                {activeTab === 'summary' ? (
                  <div className="h-full overflow-auto p-6">
                    {renderSummaryTable(getSynthesis())}
                    {getSynthesis().length === 0 &&
                      currentData?.monthlyData?.[0]?.data && (
                        <div>
                          <p className="mb-4 text-sm text-neutral-500">
                            {t(
                              'editions.visualization.noSynthesis',
                              'Pas de synthese disponible, affichage du premier mois'
                            )}
                          </p>
                          {renderTable(currentData.monthlyData[0].data)}
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="flex h-full flex-col">
                    {/* Month Navigation */}
                    <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSelectedMonthIndex(
                            Math.max(0, selectedMonthIndex - 1)
                          )
                        }
                        disabled={selectedMonthIndex === 0}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      <div className="text-center">
                        {currentData?.monthlyData?.[selectedMonthIndex] && (
                          <>
                            <h3 className="text-lg font-semibold text-neutral-900">
                              {getMonthLabel(
                                currentData.monthlyData[selectedMonthIndex]
                                  .month,
                                currentData.monthlyData[selectedMonthIndex].year
                              )}
                            </h3>
                            <p className="text-sm text-neutral-500">
                              {selectedMonthIndex + 1} /{' '}
                              {currentData.monthlyData.length}
                            </p>
                          </>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSelectedMonthIndex(
                            Math.min(
                              (currentData?.monthlyData?.length || 1) - 1,
                              selectedMonthIndex + 1
                            )
                          )
                        }
                        disabled={
                          !currentData?.monthlyData ||
                          selectedMonthIndex ===
                            currentData.monthlyData.length - 1
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Month Data */}
                    <div className="flex-1 overflow-auto p-6">
                      {currentData?.monthlyData?.[selectedMonthIndex]?.data &&
                        renderTable(
                          currentData.monthlyData[selectedMonthIndex].data
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
